import React, { Component } from "react";
import { InputNumber, Input, Button, Tooltip, Select, DatePicker } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";

const { Option } = Select;

/**
 * TablaEditor — grilla estilo Excel con tipo de columna configurable.
 *
 * Props:
 *   columnas:      { nombre: string, tipo: 'numero'|'texto'|'fecha' }[]
 *   filas:         { key: number, valores: any[] }[]
 *   onChange:      ({ columnas, filas }) => void
 *   readOnly:      bool  — vista de solo lectura
 *   columnasFijas: bool  — no permite agregar/eliminar/renombrar/retipificar columnas
 */

const cellStyle = {
    border: "1px solid #d9d9d9",
    padding: 0,
    minWidth: 130,
};

const headerCellStyle = {
    ...cellStyle,
    backgroundColor: "#fafafa",
    fontWeight: 600,
    verticalAlign: "top",
};

const indexCellStyle = {
    border: "1px solid #d9d9d9",
    backgroundColor: "#fafafa",
    padding: "2px 6px",
    textAlign: "center",
    color: "#888",
    fontSize: 12,
    minWidth: 36,
    userSelect: "none",
};

const TIPO_LABELS = { numero: "Número", texto: "Texto", fecha: "Fecha" };
const TIPO_COLORS = { numero: "#1677ff", texto: "#389e0d", fecha: "#d46b08" };

class TablaEditor extends Component {

    // ── Columnas ─────────────────────────────────────────────────

    agregarColumna() {
        const { columnas, filas } = this.props;
        const nuevaColumna = { nombre: "", tipo: "numero" };
        const nuevasFilas = filas.map((f) => ({ ...f, valores: [...f.valores, null] }));
        this.props.onChange({ columnas: [...columnas, nuevaColumna], filas: nuevasFilas });
    }

    eliminarColumna(idx) {
        const { columnas, filas } = this.props;
        const nuevasColumnas = columnas.filter((_, i) => i !== idx);
        const nuevasFilas = filas.map((f) => ({
            ...f,
            valores: f.valores.filter((_, i) => i !== idx),
        }));
        this.props.onChange({ columnas: nuevasColumnas, filas: nuevasFilas });
    }

    actualizarColumna(idx, campo, valor) {
        const columnas = this.props.columnas.map((c, i) =>
            i === idx ? { ...c, [campo]: valor } : c
        );
        // Si cambia el tipo, limpiar los valores de esa columna en todas las filas
        let filas = this.props.filas;
        if (campo === "tipo") {
            filas = filas.map((f) => {
                const valores = [...f.valores];
                valores[idx] = null;
                return { ...f, valores };
            });
        }
        this.props.onChange({ columnas, filas });
    }

    // ── Filas ─────────────────────────────────────────────────────

    agregarFila() {
        const { columnas, filas } = this.props;
        const nuevaFila = { key: Date.now(), valores: columnas.map(() => null) };
        this.props.onChange({ columnas, filas: [...filas, nuevaFila] });
    }

    eliminarFila(key) {
        const filas = this.props.filas.filter((f) => f.key !== key);
        this.props.onChange({ columnas: this.props.columnas, filas });
    }

    actualizarValor(key, colIdx, valor) {
        const filas = this.props.filas.map((f) => {
            if (f.key !== key) return f;
            const valores = [...f.valores];
            valores[colIdx] = valor;
            return { ...f, valores };
        });
        this.props.onChange({ columnas: this.props.columnas, filas });
    }

    // ── Render celda según tipo ───────────────────────────────────

    renderCelda(fila, colIdx, tipo, readOnly) {
        const valor = fila.valores[colIdx];

        if (readOnly) {
            if (tipo === "fecha") {
                return (
                    <div style={{ padding: "4px 8px" }}>
                        {valor ? moment(valor).format("DD/MM/YYYY") : ""}
                    </div>
                );
            }
            return (
                <div style={{ padding: "4px 8px", textAlign: tipo === "numero" ? "right" : "left" }}>
                    {valor ?? ""}
                </div>
            );
        }

        if (tipo === "numero") {
            return (
                <InputNumber
                    size="small"
                    value={valor}
                    bordered={false}
                    style={{ width: "100%" }}
                    onChange={(v) => this.actualizarValor(fila.key, colIdx, v)}
                />
            );
        }

        if (tipo === "texto") {
            return (
                <Input
                    size="small"
                    value={valor ?? ""}
                    bordered={false}
                    style={{ width: "100%" }}
                    onChange={(e) => this.actualizarValor(fila.key, colIdx, e.target.value)}
                />
            );
        }

        if (tipo === "fecha") {
            return (
                <DatePicker
                    size="small"
                    value={valor ? moment(valor) : null}
                    bordered={false}
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    onChange={(date) =>
                        this.actualizarValor(fila.key, colIdx, date ? date.toISOString() : null)
                    }
                />
            );
        }

        return null;
    }

    // ── Render ───────────────────────────────────────────────────

    render() {
        const { columnas, filas, readOnly, columnasFijas } = this.props;
        const bloqueado = readOnly || columnasFijas;

        return (
            <div>
                {/* ── Botones ── */}
                {!readOnly && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        {!columnasFijas && (
                            <Button size="small" type="dashed" icon={<PlusOutlined />}
                                onClick={this.agregarColumna.bind(this)}>
                                Agregar columna
                            </Button>
                        )}
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                            onClick={this.agregarFila.bind(this)}
                            disabled={columnas.length === 0}>
                            Agregar fila
                        </Button>
                    </div>
                )}

                {/* ── Grilla ── */}
                <div style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "auto" }}>
                        <thead>
                            <tr>
                                <th style={indexCellStyle} />

                                {columnas.map((col, colIdx) => (
                                    <th key={colIdx} style={headerCellStyle}>
                                        {bloqueado ? (
                                            // Solo lectura o columnas fijas: mostrar nombre + badge de tipo
                                            <div style={{ padding: "4px 8px" }}>
                                                <div>{col.nombre || <span style={{ color: "#bbb" }}>—</span>}</div>
                                                <div style={{ fontSize: 10, color: TIPO_COLORS[col.tipo], marginTop: 2 }}>
                                                    {TIPO_LABELS[col.tipo]}
                                                </div>
                                            </div>
                                        ) : (
                                            // Editable: input nombre + selector de tipo + botón eliminar
                                            <div style={{ padding: "2px 4px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <Input
                                                        size="small"
                                                        value={col.nombre}
                                                        placeholder={`Col ${colIdx + 1}`}
                                                        bordered={false}
                                                        style={{ fontWeight: 600, flex: 1, minWidth: 60 }}
                                                        onChange={(e) => this.actualizarColumna(colIdx, "nombre", e.target.value)}
                                                    />
                                                    <Tooltip title="Eliminar columna">
                                                        <Button
                                                            size="small" type="text" danger
                                                            icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                                                            style={{ padding: "0 2px", flexShrink: 0 }}
                                                            onClick={() => this.eliminarColumna(colIdx)}
                                                        />
                                                    </Tooltip>
                                                </div>
                                                {/* Selector de tipo debajo del nombre */}
                                                <Select
                                                    size="small"
                                                    value={col.tipo}
                                                    bordered={false}
                                                    style={{ width: "100%", fontSize: 11, color: TIPO_COLORS[col.tipo] }}
                                                    onChange={(v) => this.actualizarColumna(colIdx, "tipo", v)}
                                                >
                                                    <Option value="numero">
                                                        <span style={{ color: TIPO_COLORS.numero }}>Número</span>
                                                    </Option>
                                                    <Option value="texto">
                                                        <span style={{ color: TIPO_COLORS.texto }}>Texto</span>
                                                    </Option>
                                                    <Option value="fecha">
                                                        <span style={{ color: TIPO_COLORS.fecha }}>Fecha</span>
                                                    </Option>
                                                </Select>
                                            </div>
                                        )}
                                    </th>
                                ))}

                                {!readOnly && <th style={indexCellStyle} />}
                            </tr>
                        </thead>
                        <tbody>
                            {filas.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={columnas.length + (readOnly ? 1 : 2)}
                                        style={{ textAlign: "center", padding: "16px 0", color: "#bbb", border: "1px solid #d9d9d9" }}
                                    >
                                        {columnas.length === 0
                                            ? "Agrega columnas primero"
                                            : "Sin filas — usa el botón para agregar"}
                                    </td>
                                </tr>
                            )}
                            {filas.map((fila, filaIdx) => (
                                <tr key={fila.key}>
                                    <td style={indexCellStyle}>{filaIdx + 1}</td>

                                    {columnas.map((col, colIdx) => (
                                        <td key={colIdx} style={cellStyle}>
                                            {this.renderCelda(fila, colIdx, col.tipo, readOnly)}
                                        </td>
                                    ))}

                                    {!readOnly && (
                                        <td style={indexCellStyle}>
                                            <Tooltip title="Eliminar fila">
                                                <Button
                                                    size="small" type="text" danger
                                                    icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                                                    onClick={() => this.eliminarFila(fila.key)}
                                                />
                                            </Tooltip>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!readOnly && (
                    <div style={{ color: "#888", fontSize: 11, marginTop: 6 }}>
                        {columnas.length} columna{columnas.length !== 1 ? "s" : ""} · {filas.length} fila{filas.length !== 1 ? "s" : ""}
                    </div>
                )}
            </div>
        );
    }
}

export default TablaEditor;